import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { Html, OrbitControls } from '@react-three/drei'
import { ViewportDidatico3D } from './ViewportDidatico3D'
import { VeiculoImpactos3D } from './VeiculoImpactos3D'
import { MotoristaImpactos3D } from './MotoristaImpactos3D'
import { amostrarQuatroImpactos, QUATRO_IMPACTOS, type OrgaoImpactos } from '../../dominio/didatico/quatroImpactos'
import { movimentoCargaEncosto } from '../../dominio/didatico/cargaContraEncosto'
import { anguloPescoco } from '../../dominio/didatico/pescocoImpactos'

export type VistaImpactos = 'geral' | 'encefalo' | 'torax' | 'abdome' | 'posterior'
export interface PropsCenaQuatroImpactos {
  tempo: number
  raioX: boolean
  foco: OrgaoImpactos | null
  vista: VistaImpactos
  aoSelecionar: (orgao: OrgaoImpactos) => void
}
const CAMERAS: Record<VistaImpactos, { posicao: [number, number, number]; alvo: [number, number, number] }> = {
  geral: { posicao: [3.4, 2.45, 4.2], alvo: [0, 0.88, 0.08] },
  encefalo: { posicao: [0.38, 1.62, 1.04], alvo: [0, 1.48, 0.15] },
  torax: { posicao: [0.5, 1.14, 1.4], alvo: [0, 1.04, 0.15] },
  abdome: { posicao: [0.45, 0.88, 1.25], alvo: [0, 0.66, 0.12] },
  posterior: { posicao: [-0.5, 0.88, -1.25], alvo: [0, 0.67, 0] },
}
function Camera({ vista }: { vista: VistaImpactos }) {
  const { camera, invalidate } = useThree()
  useEffect(() => { camera.position.set(...CAMERAS[vista].posicao); camera.lookAt(...CAMERAS[vista].alvo); invalidate() }, [vista, camera, invalidate])
  return <OrbitControls key={vista} target={CAMERAS[vista].alvo} minDistance={0.35} maxDistance={7} enableDamping />
}
function Caixa({ posicao, escala, cor, opacidade = 1 }: { posicao: [number, number, number]; escala: [number, number, number]; cor: string; opacidade?: number }) {
  return <mesh position={posicao} scale={escala}>
    <boxGeometry /><meshStandardMaterial color={cor} roughness={0.7} transparent={opacidade < 1} opacity={opacidade} depthWrite={opacidade >= 1} />
  </mesh>
}
function Seta({ z, y, cor }: { z: number; y: number; cor: string }) {
  return <group position={[0.34, y, z]} rotation={[Math.PI / 2, 0, 0]}>
    <mesh><cylinderGeometry args={[0.009, 0.009, 0.24, 8]} /><meshBasicMaterial color={cor} /></mesh>
    <mesh position={[0, 0.17, 0]}><coneGeometry args={[0.04, 0.09, 12]} /><meshBasicMaterial color={cor} /></mesh>
  </group>
}
function Cena({ tempo, raioX, foco, aoSelecionar, vista }: PropsCenaQuatroImpactos) {
  const { indice, progresso } = amostrarQuatroImpactos(tempo)
  const suave = (p: number) => p * p * (3 - 2 * p)
  const fase = (i: number) => indice > i ? 1 : indice === i ? suave(progresso) : 0
  const veiculo = fase(0), corpo = fase(1), orgaos = fase(2), objeto = fase(3)
  const interno = orgaos * 0.05
  const carga = movimentoCargaEncosto(objeto)
  const cor = QUATRO_IMPACTOS[indice].cor
  return <>
    <color attach="background" args={['#091522']} />
    <ambientLight intensity={1.4} />
    <directionalLight position={[3, 5, 4]} intensity={2.7} />
    <directionalLight position={[-2, 2, -3]} intensity={1.6} color="#80c9ff" />
    <Camera vista={vista} />
    <gridHelper args={[8, 24, '#203e52', '#172c3d']} />
    <Caixa posicao={[0, 0.56, 1.66]} escala={[1.6, 1.12, 0.19]} cor="#526075" opacidade={0.45} />
    <group position={[0, 0, -0.35 + veiculo * 0.55]}>
      <VeiculoImpactos3D distanciaRodada={veiculo * 0.55} deformacao={veiculo} inclinacaoEncosto={carga.inclinacaoEncosto} raioX={raioX} corImpacto={indice === 0 ? cor : '#638daa'} />
      <MotoristaImpactos3D carga={corpo} raioX={raioX} deslocamentoOrgaos={interno}
        anguloPescoco={anguloPescoco(corpo, orgaos, objeto)}
        foco={foco} aoSelecionar={aoSelecionar} destacarOrgaos={indice === 2} destacarCinto={indice === 1} />
      {indice === 2 && <><Seta z={0.2} y={1.48} cor={cor} /><Seta z={0.15} y={0.91} cor={cor} /></>}
      <group position={[carga.x, carga.y, carga.z]} rotation={[carga.rotacaoX, 0, carga.rotacaoZ]}>
        <Caixa posicao={[0, 0, 0]} escala={[0.22, 0.29, 0.12]} cor={indice === 3 ? cor : '#46576e'} />
        <Caixa posicao={[0, -0.04, 0.075]} escala={[0.16, 0.15, 0.035]} cor="#64748b" />
        <Html position={[0, 0.26, 0]} center><span className="qi-rotulo">Carga solta</span></Html>
      </group>
    </group>
    <Html position={[0, 0.075, 1.76]} center><span className="qi-rotulo">Frente do veículo →</span></Html>
  </>
}
export function CenaQuatroImpactos3D(props: PropsCenaQuatroImpactos) {
  return <ViewportDidatico3D posicao={CAMERAS.geral.posicao}><Cena {...props} /></ViewportDidatico3D>
}
